import { readJson, writeStored } from '@/socket/session';

/**
 * The host's Jira details, minus the credential.
 *
 * The API token is deliberately not part of this type and never reaches
 * storage. It authenticates the person against their whole Jira account and
 * does not expire on its own, so it lives in component state for as long as
 * the tab is open and is retyped after a refresh. Everything here is inert by
 * comparison: knowing someone's address and site name grants nothing.
 */

const JIRA_KEY = 'point-ker:jira';

export interface JiraSettings {
  /** Atlassian account the token belongs to. */
  readonly email: string;
  /** Site name or full host — `acme` and `acme.atlassian.net` both work. */
  readonly site: string;
  /** Field the estimate is written to; story points on most instances. */
  readonly customField: string;
}

export const DEFAULT_JIRA_SETTINGS: JiraSettings = {
  email: '',
  site: '',
  customField: 'customfield_10035',
};

export function loadJiraSettings(): JiraSettings {
  const stored = readJson<Partial<JiraSettings>>(JIRA_KEY);
  if (!stored) return DEFAULT_JIRA_SETTINGS;
  return {
    email: typeof stored.email === 'string' ? stored.email : '',
    site: typeof stored.site === 'string' ? stored.site : '',
    customField:
      typeof stored.customField === 'string' && stored.customField !== ''
        ? stored.customField
        : DEFAULT_JIRA_SETTINGS.customField,
  };
}

export function saveJiraSettings(settings: JiraSettings): void {
  writeStored(JIRA_KEY, JSON.stringify(settings));
}

/** Whether the command can be completed. The token is passed separately. */
export function isJiraReady(settings: JiraSettings, token: string): boolean {
  return (
    settings.email.trim() !== '' &&
    settings.site.trim() !== '' &&
    settings.customField.trim() !== '' &&
    token.trim() !== ''
  );
}
